import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  CompassColor,
  Spacing,
  Stack,
  Text,
} from "@noom/wax-component-library";

import { ProgressBar } from "@/components/ProgressBar";
import { FadeInAndOut } from "@/components/animation/fade";
import type {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import { RetriageFinishedStatus } from "@/constants";
import { useAppContext } from "@/contexts";
import { useGoto } from "@/hooks";

import { BaseInterstitial } from "../../core";

const QUESTION_ID = "personalizingProgramV2";

type Slide = {
  content: React.ReactElement;
  durationMs?: number;
};

const SlideText: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <Text
    as="p"
    fontFamily="Untitled Serif"
    fontWeight={500}
    fontSize="28px"
    lineHeight="36px"
    letterSpacing="0px"
    textAlign="center"
  >
    {children}
  </Text>
);

const PROGRESS_BAR_DURATION = window.Cypress ? 500 : 4000;
const SLIDE_DURATION_MS = window.Cypress ? 500 : 2000;

const PersonalizingProgramV2: React.FC<QuestionComponentProps> = ({
  onClickBack,
  onClickNext,
  surveyAnswers,
}) => {
  const {
    enrollmentType,
    qualifiedPrograms,
    retriageEligibility,
    selectedProgram,
    setSelectedProgram,
  } = useAppContext();
  const { retriageFinished } = useGoto();
  const [currentSlideIdx, setCurrentSlideIdx] = useState(0);
  useTranslation();

  const dppEligible = qualifiedPrograms.includes("DPP");
  const noomDiabetesEligible = qualifiedPrograms.includes("DIABETES_LIFESTYLE");

  // Currently not shown in the Med path, so we only need to check for DPP and Noom Diabetes eligibility.
  const isRetriageWithoutQualification =
    enrollmentType === "RETRIAGE" && !(dppEligible || noomDiabetesEligible);

  const slides: Slide[] = [
    {
      content: (
        <Stack spacing={Spacing[16]} alignItems="center">
          <SlideText>
            <Trans i18nKey="personalizationProgramV2.analyzing" />
          </SlideText>
          <ProgressBar
            totalTimeMs={PROGRESS_BAR_DURATION}
            borderRadius="60px"
            height="24px"
            trackColor={CompassColor.sand01}
          />
        </Stack>
      ),
      durationMs: PROGRESS_BAR_DURATION,
    },
    {
      content: (
        <FadeInAndOut duration={SLIDE_DURATION_MS / 1000}>
          <SlideText>
            <Trans
              i18nKey="personalizationProgramV2.greeting"
              values={{
                firstName:
                  surveyAnswers.eligibilityCheck?.firstName ||
                  retriageEligibility?.userProfile?.firstName ||
                  retriageEligibility?.eligibilityProperties?.firstName,
              }}
            />
          </SlideText>
        </FadeInAndOut>
      ),
      durationMs: SLIDE_DURATION_MS,
    },
    {
      content: (
        <FadeInAndOut
          duration={SLIDE_DURATION_MS / 1000}
          delay={SLIDE_DURATION_MS / 1000}
        >
          <SlideText>
            <Trans i18nKey="personalizationProgramV2.personalizedPlan" />
          </SlideText>
        </FadeInAndOut>
      ),
      durationMs: SLIDE_DURATION_MS,
    },
    {
      content: (
        <FadeInAndOut
          duration={SLIDE_DURATION_MS / 1000}
          delay={(SLIDE_DURATION_MS * 2) / 1000}
        >
          <SlideText>
            <Trans i18nKey="personalizationProgramV2.reachGoals" />
          </SlideText>
        </FadeInAndOut>
      ),
      durationMs: SLIDE_DURATION_MS,
    },
  ];

  useEffect(() => {
    if (["MED", "MOOD", "NMPBA"].includes(selectedProgram || "")) {
      return;
    }

    // place user in highest eligible program
    if (noomDiabetesEligible) {
      setSelectedProgram("DIABETES_LIFESTYLE");
    } else if (dppEligible) {
      setSelectedProgram("DPP");
    } else {
      setSelectedProgram("WEIGHT");
    }
  }, []);

  useEffect(() => {
    // If in retriage and not qualified for any program, will only show first slide
    if (
      currentSlideIdx < slides.length - 1 &&
      !isRetriageWithoutQualification
    ) {
      const timer = setTimeout(() => {
        setCurrentSlideIdx(currentSlideIdx + 1);
      }, slides[currentSlideIdx].durationMs);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => {
      if (isRetriageWithoutQualification) {
        retriageFinished(RetriageFinishedStatus.ALREADY_IN_BEST_PROGRAM);
        return;
      }
      onClickNext(null, { updatedProgram: selectedProgram });
    }, slides[currentSlideIdx].durationMs);
    return () => clearTimeout(timer);
  }, [currentSlideIdx]);

  return (
    <BaseInterstitial
      onClickBack={onClickBack}
      onClickNext={onClickNext}
      questionId={QUESTION_ID}
      backgroundColor={CompassColor.offWhite}
      centerChildren
    >
      {slides[currentSlideIdx].content}
    </BaseInterstitial>
  );
};

export const Q_PERSONALIZING_PROGRAM_V2: QuestionDefinition = {
  id: QUESTION_ID,
  supportedPrograms: ["WEIGHT", "DPP", "DIABETES_LIFESTYLE"],
  shouldShowQuestion: () => true,
  component: PersonalizingProgramV2,
};
